import { Typography } from '@mui/material';
import classNames from 'classnames';
import { uniqBy, upperFirst } from 'lodash-es';
import bind from 'memoize-bind';
import moment from 'moment-timezone';
import React, { Component, createRef, Fragment } from 'react';
import MaskedInput from 'react-text-mask';
import { createNumberMask } from 'text-mask-addons';
import validator from 'validator';

import { TagSchema } from '@sbu/hooks.tags';

import { ClickLink } from 'shared/components/internal/ClickLink';
import { getContentTypes, TagInput } from 'shared/components/internal/Tags';
import {
  dateMask,
  dateRangeDivider,
  meridiemMask,
  numberMask,
  timeMask,
} from 'shared/utils/masks';

import { OnSelectValueProps } from '../FilterDropdown';
import {
  AllOperators,
  DateField,
  DateFilterModifier,
  FieldTypes,
  FilterTypes,
  KeywordFilter,
  ListField,
  ListFieldOption,
  Modifier,
  NegatableListFieldOption,
  NumericField,
  PropBasedFilterTypes,
  Source,
  SuggestedFilter,
  ValueBasedFilterTypes,
} from '../types';
import {
  flattenOptionGroups,
  getDateFormat,
  getFieldObject,
  getOperatorObject,
  getRangeDatesFromInput,
  isDateRangeFilter,
  isKeyValueFilter,
  isRelativeRangeFilter,
  needNumberFormat,
  ProcessedField,
} from '../utils';
import css from './FilterInput.module.scss';

interface FilterInputProps {
  disabled?: boolean;
  view?: 'source' | 'fields' | 'operators' | 'value' | 'modifier';
  value?: string;
  variant: 'default' | 'compact';
  filteredOptions: ListFieldOption[] | NegatableListFieldOption[];
  partialFilter: Partial<FilterTypes>;
  filters: FilterTypes[];
  partialFilterSelected: boolean;
  partialModifier: Partial<DateFilterModifier>;
  fields: FieldTypes[];
  activeOptionIndex: number;
  suggestedFilters: SuggestedFilter[];
  showTime: boolean;
  includeSeconds: boolean;
  allowMultiple: boolean;
  filteredFieldsList: FieldTypes[];
  filteredOperatorsList: any;
  filteredModifiersList: Modifier[];
  flatProcessedFields: ProcessedField[];
  placeholderText: string;
  sources: Source[];
  allSources: Source[];
  activeSource: string;
  autocompleteList: string[];
  tagAutocompleteList?: string[];
  tagSchemas?: TagSchema[];
  onSelectSource: (source: string) => void;
  onToggleFieldVisibility: (prop: string) => void;
  onInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onActiveIndexChange: (index: number) => void;
  onSetOperatorsView: () => void;
  onSetValueView: () => void;
  onSetFieldsView: () => void;
  onClose: () => void;
  onSelectPartial: () => void;
  onSelectField: (field: string) => void;
  onSelectOperator: (operator: AllOperators) => void;
  onSelectValue: (props: OnSelectValueProps) => void;
  onSelectModifier: (unit: Modifier) => void;
  onDateSelection: (value: Date, endValue?: Date) => void;
}

const actionKeys = [
  'ArrowUp',
  'ArrowDown',
  'Escape',
  'Enter',
  'Tab',
  'Backspace',
];

const modifierMask = createNumberMask({
  prefix: '',
  allowNegative: false,
  integerLimit: 4,
});

export class FilterInput extends Component<FilterInputProps> {
  containerRef = createRef<HTMLDivElement>();
  inputRef = createRef<MaskedInput>();

  componentDidUpdate() {
    // https://github.com/text-mask/text-mask/pull/831
    if (!this.props.value && this.inputRef.current) {
      (this.inputRef.current.inputElement as HTMLInputElement).value = '';
    }
  }

  getRef() {
    return this.showTagInput() ? this.containerRef.current : this.getInputRef();
  }

  getInputRef() {
    return this.inputRef.current?.inputElement as HTMLInputElement;
  }

  getInputText() {
    return this.getInputRef().value;
  }

  // complexity 15
  // eslint-disable-next-line complexity
  getPlaceholderText() {
    const {
      view,
      suggestedFilters,
      fields,
      filters,
      allowMultiple,
      showTime,
      includeSeconds,
      placeholderText,
    } = this.props;

    const suggestionsCount =
      suggestedFilters &&
      suggestedFilters.filter((s) => !s.view || s.view === 'field').length;

    if (!allowMultiple && filters.length === 1) {
      return '';
    } else if (!view) {
      return placeholderText;
    } else if (view === 'source') {
      return 'Select a source to filter on...';
    } else if (view === 'fields') {
      if (suggestionsCount) {
        return 'Select a field to filter on or pick a suggestion...';
      } else {
        return 'Select a field to filter on...';
      }
    } else if (view === 'value') {
      const partialFilter = this.props.partialFilter as ValueBasedFilterTypes;

      if (partialFilter && partialFilter.prop) {
        const field = getFieldObject(partialFilter.prop, fields);

        if (field && field.type === 'date') {
          const dateTimePlaceholder = getDateFormat(showTime, includeSeconds);

          if (isDateRangeFilter(partialFilter)) {
            return `${dateTimePlaceholder} - ${dateTimePlaceholder}`;
          } else if (!isRelativeRangeFilter(partialFilter)) {
            return dateTimePlaceholder;
          }
        }
      }
    }

    return '';
  }

  // complexity 13
  // eslint-disable-next-line complexity
  getInputMask() {
    const { view, filteredFieldsList, showTime } = this.props;

    if (view === 'value') {
      const partialFilter = this.props.partialFilter as PropBasedFilterTypes;

      if (partialFilter && partialFilter.prop) {
        const field = getFieldObject(partialFilter.prop, filteredFieldsList);
        const isRange = isRelativeRangeFilter(partialFilter);

        if (field) {
          if (field.type === 'date' && !isRange) {
            const dateTimeMask = !showTime
              ? dateMask
              : dateMask.concat(timeMask, meridiemMask);

            if (partialFilter.operator === 'between') {
              return dateTimeMask.concat(dateRangeDivider, dateTimeMask);
            } else {
              return dateTimeMask;
            }
          } else if (field.type === 'date' && isRange) {
            return modifierMask;
          } else if (field.type === 'numeric' && needNumberFormat(field.prop)) {
            return numberMask;
          }
        }
      }
    }

    return false;
  }

  getShowMask() {
    const partialFilter = this.props.partialFilter as ValueBasedFilterTypes;
    let isNumeric = false;

    if (partialFilter && partialFilter.prop) {
      const field = getFieldObject(
        partialFilter.prop,
        this.props.filteredFieldsList
      );
      isNumeric = !!field && field.type === 'numeric';
    }

    return !isRelativeRangeFilter(partialFilter) && !isNumeric;
  }

  // complexity 22
  // eslint-disable-next-line complexity
  getDateFromInputText(inputText: string) {
    const { filteredFieldsList, showTime, includeSeconds } = this.props;
    const partialFilter = this.props.partialFilter as ValueBasedFilterTypes;
    const fieldObject = getFieldObject(partialFilter.prop, filteredFieldsList);

    // Only dates are applicable
    if (!fieldObject || fieldObject.type !== 'date') {
      return;
    }

    const isRelativeRange = isRelativeRangeFilter(partialFilter);
    const isSpecificDateRange = isDateRangeFilter(partialFilter);

    if (!isRelativeRange && !isSpecificDateRange) {
      // Only include complete dates from the text mask
      if (inputText.indexOf('_') === -1) {
        const format = getDateFormat(showTime, includeSeconds);
        const dateField = fieldObject as DateField;
        let momentVal = moment(inputText, format);

        if (momentVal.isValid()) {
          let now = moment(new Date());

          if (!showTime) {
            momentVal = momentVal.startOf('day');
            now = now.startOf('day');
          }

          const isAfter = dateField.disableFuture && momentVal.isAfter(now);
          const isBefore = dateField.disablePast && momentVal.isBefore(now);

          if (!isAfter && !isBefore) {
            return {
              dateSelection: momentVal.toDate(),
            };
          }
        }
      }
    } else if (isSpecificDateRange) {
      const format = getDateFormat(showTime, includeSeconds);
      const dateField = fieldObject as DateField;
      const { rangeStart, rangeEnd } = getRangeDatesFromInput(
        inputText,
        format
      );

      if (rangeStart.isValid() && rangeEnd.isValid()) {
        let now = moment(new Date());
        if (!showTime) {
          now = now.startOf('day');
        }

        const isAfter =
          dateField.disableFuture &&
          (rangeStart.isAfter(now) || rangeEnd.isAfter(now));
        const isBefore =
          dateField.disablePast &&
          (rangeStart.isBefore(now) || rangeEnd.isBefore(now));

        if (!isAfter && !isBefore) {
          return {
            dateSelection: rangeStart.toDate(),
            dateRangeEndSelection: rangeEnd.toDate(),
          };
        }
      }
    }
  }

  focusInput() {
    // wait for dom to settle
    setTimeout(() => {
      if (this.inputRef && this.inputRef.current) {
        this.inputRef.current.inputElement.focus();
      }
    }, 100);
  }

  checkNumberValueWithinRange(event: React.KeyboardEvent<HTMLInputElement>) {
    const { filteredFieldsList, partialFilter } = this.props;
    const partialPropFilter = partialFilter as PropBasedFilterTypes;
    const field = getFieldObject(
      partialPropFilter.prop,
      filteredFieldsList
    ) as NumericField;
    const target = event.target as HTMLInputElement;

    if (
      field &&
      field.type === 'numeric' &&
      (field.min !== undefined || field.max !== undefined)
    ) {
      const nextNum = parseFloat(event.key);
      if (!isNaN(nextNum)) {
        const inputNum = parseFloat(`${target.value}${nextNum}`);

        if (
          (field.min !== undefined && inputNum < field.min) ||
          (field.max !== undefined && inputNum > field.max)
        ) {
          event.preventDefault();
          event.stopPropagation();
        }
      }
    }
  }

  showTagInput() {
    const { view, partialFilter } = this.props;
    return (
      view === 'value' &&
      isKeyValueFilter(partialFilter as ValueBasedFilterTypes)
    );
  }

  onPartialValueClick() {
    this.props.onSetValueView();
  }

  onInputChange(event: React.ChangeEvent<HTMLInputElement>) {
    const { onSetFieldsView, onSetOperatorsView, onInputChange } = this.props;
    const inputText = this.getInputText();
    const inputIsEmpty = !inputText || inputText === '';
    const partialFilter = this.props.partialFilter as ValueBasedFilterTypes;

    if (inputIsEmpty && !partialFilter.prop) {
      onSetFieldsView();
    } else if (inputIsEmpty && !partialFilter.operator) {
      onSetOperatorsView();
    }

    onInputChange(event);
  }

  onInputKeydown(event: React.KeyboardEvent<HTMLInputElement>) {
    const { view, partialFilter, onSetFieldsView } = this.props;
    const key = event.key;

    if (actionKeys.includes(key)) {
      if (key !== 'Tab' && key !== 'Backspace') {
        event.preventDefault();
      }

      // Run generic operations
      const keyFn = `onInputKeydown${key}`;
      this[keyFn] && this[keyFn](event);

      if (view) {
        // Run type specific operations
        const viewName = upperFirst(view);
        const keyNameFn = `on${viewName}InputKeydown${key}`;
        this[keyNameFn] && this[keyNameFn](event);
      } else {
        onSetFieldsView();
      }
    }

    if (view === 'value') {
      const target = event.currentTarget;

      // Prevent users entering 0, text mask handles the rest
      if (isRelativeRangeFilter(partialFilter)) {
        if (target.value === '' && event.key === '0') {
          event.preventDefault();
          event.stopPropagation();
        }
      } else {
        this.checkNumberValueWithinRange(event);
      }
    }
  }

  onSourceInputKeydownEnter() {
    const { activeOptionIndex, sources, onSelectSource } = this.props;

    if (activeOptionIndex > -1) {
      const source = sources[activeOptionIndex];
      if (source) {
        onSelectSource(source.prop);
      }
    }
  }

  onSourceInputKeydownTab(event: React.KeyboardEvent) {
    event.preventDefault();

    const { sources, onSelectSource } = this.props;
    const inputText = this.getInputText().toLowerCase();

    if (!inputText) {
      return;
    }

    const source = sources.find(
      (u) => u.label.toLowerCase().indexOf(inputText) === 0
    );

    if (source) {
      onSelectSource(source.prop);
    }
  }

  onFieldsInputKeydownEnter() {
    const {
      activeOptionIndex,
      flatProcessedFields,
      onToggleFieldVisibility,
      onSelectField,
      onSelectValue,
      allSources,
      activeSource,
    } = this.props;
    const inputText = this.getInputText();

    if (activeOptionIndex > -1) {
      const field = flatProcessedFields[activeOptionIndex];
      if (field.children) {
        onToggleFieldVisibility(field.prop);
      } else {
        onSelectField(field.prop);
      }
      return;
    }

    if (!inputText || inputText === '') {
      return;
    }

    // Determine if our source is keyword searchable, record
    // sources are not keywordable
    const source = allSources.find((s) => s.prop === activeSource);
    const canSelect = !source || source.allowKeywords !== false;

    // Handles the keyword scenario...
    if (canSelect) {
      onSelectValue({ value: inputText });
    }
  }

  onOperatorsInputKeydownEnter() {
    const {
      partialFilter,
      activeOptionIndex,
      fields,
      filteredOperatorsList,
      onSelectOperator,
    } = this.props;
    const inputText = this.getInputText();

    if (activeOptionIndex > -1) {
      onSelectOperator(filteredOperatorsList[activeOptionIndex].type);
    }

    if (!inputText || inputText === '') {
      return;
    }

    const operatorObject = getOperatorObject(
      inputText,
      partialFilter as PropBasedFilterTypes,
      fields
    );
    if (operatorObject) {
      onSelectOperator(operatorObject.type);
    }
  }

  onModifierInputKeydownEnter() {
    const { activeOptionIndex, filteredModifiersList } = this.props;
    const inputText = this.getInputText();

    if (activeOptionIndex > -1) {
      this.props.onSelectModifier(filteredModifiersList[activeOptionIndex]);
    }

    if (!inputText || inputText === '') {
      return;
    }

    const matchedModifier = filteredModifiersList.find(
      (modifier) => inputText.toLowerCase() === modifier.label.toLowerCase()
    );
    if (matchedModifier) {
      this.props.onSelectModifier(matchedModifier);
    }
  }

  onInputKeyup(event: React.KeyboardEvent<HTMLInputElement>) {
    const inputElement = event.currentTarget;
    const inputText = inputElement.value;

    const date = this.getDateFromInputText(inputText);
    if (date) {
      this.props.onDateSelection(
        date.dateSelection,
        date.dateRangeEndSelection
      );
    }
  }

  // complexity 20
  // eslint-disable-next-line complexity
  onValueInputKeydownEnter() {
    const {
      activeOptionIndex,
      filteredOptions,
      onSelectValue,
      autocompleteList,
    } = this.props;

    if (activeOptionIndex > -1) {
      if (filteredOptions.length) {
        const groups = uniqBy(filteredOptions, (o) => o.group || '').map(
          (o) => o.group
        ) as string[];
        const isGrouped = !!groups.filter((g) => g !== undefined).length;

        let prop = filteredOptions[activeOptionIndex].prop;

        // Groups could re-arrange the options, need to get the actual value
        if (isGrouped && groups.length > 1) {
          const resortedGroups = flattenOptionGroups(filteredOptions, groups);
          prop = resortedGroups[activeOptionIndex].prop;
        }

        onSelectValue({ value: prop });
        return;
      } else if (autocompleteList.length) {
        onSelectValue({ value: autocompleteList[activeOptionIndex] });
        return;
      }
    }

    const inputText = this.getInputText();
    if (!inputText || inputText === '') {
      return;
    }

    const { filteredFieldsList } = this.props;
    const partialFilter = this.props.partialFilter as ValueBasedFilterTypes;
    const fieldObject = getFieldObject(partialFilter.prop, filteredFieldsList);
    const isRelativeRange = isRelativeRangeFilter(partialFilter);

    if (!fieldObject) {
      onSelectValue({ value: inputText });
    } else if (fieldObject.type === 'ip') {
      if (['equals', 'doesNotEqual'].includes(partialFilter.operator)) {
        const valid = validator.isIP(inputText.trim());
        if (valid) {
          onSelectValue({ value: inputText });
        }
      } else if (
        ['isInIpRange', 'isNotInIpRange'].includes(partialFilter.operator)
      ) {
        const valid = validator.isIPRange(inputText.trim());
        if (valid) {
          onSelectValue({ value: inputText });
        }
      }
    } else if (fieldObject.type === 'list') {
      const listFieldObject = fieldObject as ListField;
      if (listFieldObject.options) {
        const option = filteredOptions.find(
          (f) => f.label.toLowerCase() === inputText.toLowerCase()
        );

        if (option) {
          onSelectValue({ value: option.prop });
        }
      } else {
        onSelectValue({ value: inputText });
      }
    } else if (fieldObject.type === 'date') {
      if (isRelativeRange) {
        onSelectValue({ value: Number(inputText) });
      } else {
        const date = this.getDateFromInputText(inputText);
        if (date) {
          onSelectValue({
            value: date.dateSelection,
            endValue: date.dateRangeEndSelection,
          });
        }
      }
    } else {
      onSelectValue({ value: inputText });
    }
  }

  onInputKeydownArrowUp() {
    let { activeOptionIndex, onActiveIndexChange } = this.props;

    if (activeOptionIndex - 1 >= -1) {
      onActiveIndexChange(--activeOptionIndex);
    }
  }

  onInputKeydownArrowDown() {
    const {
      view,
      flatProcessedFields,
      filteredOperatorsList,
      filteredOptions,
      filteredModifiersList,
      sources,
      onActiveIndexChange,
      autocompleteList,
    } = this.props;
    let { activeOptionIndex } = this.props;
    let maxOptionIndex: number = 0;

    if (view === 'source') {
      maxOptionIndex = sources.length;
    } else if (view === 'fields') {
      maxOptionIndex = flatProcessedFields.length;
    } else if (view === 'operators') {
      maxOptionIndex = filteredOperatorsList.length;
    } else if (view === 'value') {
      if (autocompleteList.length) {
        maxOptionIndex = autocompleteList.length;
      } else {
        maxOptionIndex = filteredOptions.length;
      }
    } else if (view === 'modifier') {
      maxOptionIndex = filteredModifiersList.length;
    }

    if (activeOptionIndex + 1 < maxOptionIndex) {
      onActiveIndexChange(++activeOptionIndex);
    }
  }

  onInputKeydownEscape() {
    this.props.onClose();
  }

  onTagInputBlur() {
    this.props.onClose();
  }

  onFieldsInputKeydownTab(event: React.KeyboardEvent) {
    const { filteredFieldsList, onClose } = this.props;
    const inputText = this.getInputText().toLowerCase().trim();

    if (!inputText || inputText === '') {
      onClose();
      return;
    }

    event.preventDefault();

    const field = filteredFieldsList.find(
      (field) =>
        field.label !== undefined &&
        field.label.toLowerCase().indexOf(inputText) === 0
    );

    if (field) {
      this.props.onSelectField(field.prop);
    }
  }

  onModifierInputKeydownTab(event: React.KeyboardEvent) {
    event.preventDefault();

    const { filteredModifiersList, onSelectModifier } = this.props;
    const inputText = this.getInputText().toLowerCase();

    if (!inputText || inputText === '') {
      return;
    }

    const unit = filteredModifiersList.find(
      (u) => u.label.toLowerCase().indexOf(inputText) === 0
    );

    if (unit) {
      onSelectModifier(unit);
    }
  }

  onOperatorsInputKeydownTab(event: React.KeyboardEvent) {
    event.preventDefault();

    const { filteredOperatorsList } = this.props;
    const inputText = this.getInputText().toLowerCase();
    const partialFilter = this.props.partialFilter as ValueBasedFilterTypes;

    if (!inputText || inputText === '') {
      return;
    }

    if (partialFilter.prop) {
      const operator = filteredOperatorsList.find(
        (operator) => operator.label.toLowerCase().indexOf(inputText) === 0
      );

      if (operator) {
        this.props.onSelectOperator(operator.type);
      }
    }
  }

  onValueInputKeydownTab(event: React.KeyboardEvent) {
    event.preventDefault();

    const { filteredFieldsList } = this.props;
    const inputText = this.getInputText().toLowerCase();
    const partialFilter = this.props.partialFilter as ValueBasedFilterTypes;

    if (!inputText || inputText === '') {
      return;
    }

    if (partialFilter.prop) {
      const fieldObject = getFieldObject(
        partialFilter.prop,
        filteredFieldsList
      );

      if (fieldObject) {
        if (fieldObject.type === 'list') {
          const listField = fieldObject as ListField;
          if (listField.options) {
            const option = listField.options.find(
              (option) => option.label.toLowerCase().indexOf(inputText) === 0
            );

            if (option) {
              this.props.onSelectValue({ value: option.prop });
            }
          } else {
            this.props.onSelectValue({ value: inputText });
          }
        } else if (
          fieldObject.type === 'date' &&
          isRelativeRangeFilter(partialFilter)
        ) {
          this.props.onSelectValue({ value: inputText });
        }
      }
    }
  }

  onInputKeydownBackspace(event: React.KeyboardEvent) {
    const { partialFilterSelected, view } = this.props;
    const inputElement = event.target as HTMLInputElement;
    const hasSelection =
      inputElement.selectionStart !== inputElement.selectionEnd;
    const cursorIsAtStart = inputElement.selectionStart === 0;
    const keywordFilter = this.props.partialFilter as KeywordFilter;

    if (!hasSelection && cursorIsAtStart && !partialFilterSelected) {
      this.props.onSelectPartial();
    } else if (!hasSelection && partialFilterSelected) {
      if (view === 'operators') {
        this.props.onSetFieldsView();
      } else if (view === 'value' && keywordFilter.operator !== 'keyword') {
        this.props.onSetOperatorsView();
      } else if (view === 'modifier') {
        this.props.onSetValueView();
      }
    }
  }

  onPartialOperatorClick() {
    this.props.onSetOperatorsView();
  }

  // complexity 12
  // eslint-disable-next-line complexity
  renderPartialFilter() {
    const { partialFilterSelected, filteredFieldsList, partialModifier, view } =
      this.props;
    const partialFilter = this.props.partialFilter as ValueBasedFilterTypes;

    if (partialFilter && partialFilter.prop) {
      const field = getFieldObject(partialFilter.prop, filteredFieldsList);
      const fieldLabel = field ? field.label : '';
      const operator = getOperatorObject(
        partialFilter.operator,
        partialFilter,
        filteredFieldsList
      );
      const operatorLabel = operator ? operator.label : '';

      return (
        <div className={css.partialFilter}>
          <Typography
            className={classNames(css.partialFilterField, {
              [css.partialFocused]:
                partialFilterSelected && view === 'operators',
            })}
            component='label'
            display='inline'
          >
            {fieldLabel}
          </Typography>
          {operator && view !== 'operators' && (
            <ClickLink
              className={classNames(css.partialFilterOperator, {
                [css.partialFocused]: partialFilterSelected && view === 'value',
              })}
              onClick={bind(this.onPartialOperatorClick, this)}
            >
              {operatorLabel}
            </ClickLink>
          )}
          {partialModifier.value && view === 'modifier' && (
            <ClickLink
              className={classNames(css.partialModifierValue, {
                [css.partialFocused]: partialFilterSelected,
              })}
              onClick={bind(this.onPartialValueClick, this)}
            >
              {Math.abs(partialModifier.value).toLocaleString()}
            </ClickLink>
          )}
          {partialModifier.unit && (
            <span className={css.partialModifier}>{partialModifier.unit}</span>
          )}
        </div>
      );
    }

    return null;
  }

  renderAutocompleteOverlay() {
    const {
      value,
      view,
      filteredFieldsList,
      filteredOperatorsList,
      filteredOptions,
      sources,
      filteredModifiersList,
    } = this.props;
    let autocompleteText = '';
    let showTab = false;

    if (value && view) {
      const lowerValue = value.toLowerCase().trim();
      const map = {
        source: sources,
        fields: filteredFieldsList,
        operators: filteredOperatorsList,
        value: filteredOptions,
        modifier: filteredModifiersList,
      };

      const list = map[view];
      const option = list.find(
        (item) => item.label.toLowerCase().indexOf(lowerValue) === 0
      );
      if (option) {
        showTab = true;
        autocompleteText = option.label.substr(value.length);
      }
    }

    return (
      <div className={css.autocompleteOverlay}>
        <span className={css.matchedText}>{value}</span>
        {(autocompleteText || showTab) && (
          <Fragment>
            <span className={css.autocompleteText}>{autocompleteText}</span>
            <span className={css.keyName}>tab</span>
          </Fragment>
        )}
      </div>
    );
  }

  render() {
    const {
      activeSource,
      disabled,
      onSelectValue,
      onSetOperatorsView,
      tagAutocompleteList,
      tagSchemas,
      value,
      variant,
    } = this.props;
    const showTagInput = this.showTagInput();
    const showMask = this.getShowMask();
    const mask = this.getInputMask();

    return (
      <div
        className={classNames(css.inputContainer, {
          [css.disabled]: disabled,
          [css.compactStyle]: variant === 'compact',
          [css.defaultStyle]: variant === 'default',
        })}
        ref={this.containerRef}
      >
        {this.renderPartialFilter()}
        <div className={css.inputWrapper}>
          {showTagInput && (
            <div className={css.tagInputWrapper}>
              <TagInput
                autocompleteList={tagAutocompleteList}
                autoFocus
                contentTypes={getContentTypes(activeSource)}
                disabled={disabled}
                onBlur={bind(this.onTagInputBlur, this)}
                onKeyDown={(event: React.KeyboardEvent) => {
                  if (event.key === 'Escape') {
                    this.onInputKeydownEscape();
                  } else if (event.key === 'Backspace') {
                    const inputElement = event.target as HTMLInputElement;
                    if (inputElement.name === 'tagKey' && !inputElement.value) {
                      onSetOperatorsView();
                    }
                  }
                }}
                onSetValue={(value) => onSelectValue({ value })}
                showAnyValueSchemaOption={true}
                tagSchemas={tagSchemas}
                value={value}
              />
            </div>
          )}
          {!showTagInput && (
            <Fragment>
              <MaskedInput
                autoCorrect='off'
                className={css.input}
                data-sumo-test='filter-input'
                disabled={disabled}
                guide={showMask}
                keepCharPositions={showMask}
                mask={mask}
                onChange={bind(this.onInputChange, this)}
                onKeyDown={bind(this.onInputKeydown, this)}
                onKeyUp={bind(this.onInputKeyup, this)}
                placeholder={this.getPlaceholderText()}
                ref={this.inputRef}
                showMask={showMask}
                spellCheck='false'
                type='text'
                value={value}
              />
              {this.renderAutocompleteOverlay()}
            </Fragment>
          )}
        </div>
      </div>
    );
  }
}
